import React, { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { PageHeader, BrutalCard, Chip } from "../components/brutal";
import { Trophy, Coins, Users, Sparkles, Download, Share2, Instagram } from "lucide-react";
import { toPng } from "html-to-image";
import { toast } from "sonner";

/**
 * InstagramCard — 1080x1080 square, designed to feel tactile on a phone feed.
 */
function InstagramCard({ data, forwardedRef }) {
  if (!data) return null;
  return (
    <div
      ref={forwardedRef}
      style={{
        width: 1080,
        height: 1080,
        background:
          "linear-gradient(135deg, #FF5C00 0%, #FF3AC5 55%, #0047FF 100%)",
        padding: 64,
        display: "flex",
        flexDirection: "column",
        color: "#09090B",
        fontFamily: "'Plus Jakarta Sans', sans-serif",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Noise / pattern layer */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage: "radial-gradient(circle at 10px 10px, rgba(255,255,255,0.12) 1px, transparent 0)",
          backgroundSize: "20px 20px",
        }}
      />

      {/* Brand bar */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", zIndex: 2 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div style={{
            width: 64, height: 64, background: "#FFD600", border: "4px solid #09090B",
            borderRadius: 18, display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: "8px 8px 0 0 #09090B",
          }}>
            <span style={{ fontFamily: "'Outfit'", fontWeight: 900, fontSize: 36, color: "#09090B" }}>⚡</span>
          </div>
          <div style={{ color: "#fff" }}>
            <div style={{ fontFamily: "'Outfit'", fontWeight: 900, fontSize: 38, lineHeight: 1 }}>ReferPro</div>
            <div style={{ fontSize: 16, textTransform: "uppercase", letterSpacing: 4, opacity: 0.85 }}>
              Refer · Earn · Recognise
            </div>
          </div>
        </div>
        <div style={{
          background: "#09090B", color: "#FFD600", padding: "10px 20px",
          border: "4px solid #fff", borderRadius: 100, fontWeight: 800, fontSize: 18,
          letterSpacing: 2, textTransform: "uppercase",
        }}>
          ✨ Spotlight
        </div>
      </div>

      {/* Card */}
      <div style={{
        marginTop: 48, background: "#FFFFFF", border: "6px solid #09090B",
        borderRadius: 36, boxShadow: "16px 16px 0 0 #09090B",
        padding: 48, flex: 1, display: "flex", flexDirection: "column", zIndex: 2,
      }}>
        <div style={{
          display: "inline-flex", alignItems: "center", gap: 10,
          background: "#FFD600", border: "3px solid #09090B", borderRadius: 100,
          padding: "8px 18px", fontWeight: 800, fontSize: 18, alignSelf: "flex-start",
          boxShadow: "4px 4px 0 0 #09090B", textTransform: "uppercase", letterSpacing: 2,
        }}>
          🏆 {data.headline || "Top Referrer"}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 36, marginTop: 40 }}>
          <img
            src={data.avatar_url || `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(data.name)}`}
            alt=""
            style={{
              width: 220, height: 220, borderRadius: 32, border: "6px solid #09090B",
              objectFit: "cover", boxShadow: "10px 10px 0 0 #FF5C00",
            }}
          />
          <div style={{ flex: 1 }}>
            <div style={{
              fontFamily: "'Outfit'", fontWeight: 900, fontSize: 96, lineHeight: 0.95,
              letterSpacing: "-0.03em",
            }}>
              {data.name}
            </div>
            <div style={{ marginTop: 14, fontSize: 28, fontWeight: 600, color: "#52525B" }}>
              {data.team || data.department_name || "ReferPro"}
            </div>
          </div>
        </div>

        {data.message && (
          <div style={{
            marginTop: 36, fontSize: 28, fontWeight: 500, lineHeight: 1.35,
            borderLeft: "6px solid #FF5C00", paddingLeft: 24, color: "#09090B",
          }}>
            “{data.message}”
          </div>
        )}

        <div style={{ marginTop: "auto", display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 20 }}>
          <div style={{ border: "4px solid #09090B", borderRadius: 24, padding: 24, background: "#FFD600", boxShadow: "6px 6px 0 0 #09090B" }}>
            <div style={{ fontSize: 14, textTransform: "uppercase", letterSpacing: 3, fontWeight: 800 }}>Points</div>
            <div style={{ fontFamily: "'Outfit'", fontWeight: 900, fontSize: 72, lineHeight: 1 }}>{data.points}</div>
          </div>
          <div style={{ border: "4px solid #09090B", borderRadius: 24, padding: 24, background: "#00E676", boxShadow: "6px 6px 0 0 #09090B" }}>
            <div style={{ fontSize: 14, textTransform: "uppercase", letterSpacing: 3, fontWeight: 800 }}>Referrals</div>
            <div style={{ fontFamily: "'Outfit'", fontWeight: 900, fontSize: 72, lineHeight: 1 }}>{data.total_referrals}</div>
          </div>
          <div style={{ border: "4px solid #09090B", borderRadius: 24, padding: 24, background: "#FF5C00", color: "#fff", boxShadow: "6px 6px 0 0 #09090B" }}>
            <div style={{ fontSize: 14, textTransform: "uppercase", letterSpacing: 3, fontWeight: 800 }}>Hires</div>
            <div style={{ fontFamily: "'Outfit'", fontWeight: 900, fontSize: 72, lineHeight: 1 }}>{data.hired_referrals}</div>
          </div>
        </div>
      </div>

      <div style={{ marginTop: 24, color: "#fff", fontWeight: 700, textAlign: "center", fontSize: 22, zIndex: 2 }}>
        Join us → #MadeAtReferPro
      </div>
    </div>
  );
}

/**
 * PinchZoomPreview — scales the 1080×1080 card to fit, plus allows pinch-zoom
 * on touch devices (and scroll-wheel zoom on desktop). Double-tap to reset.
 */
function PinchZoomPreview({ data, cardRef }) {
  const containerRef = useRef(null);
  const innerRef = useRef(null);
  const [baseScale, setBaseScale] = useState(0.5);
  const [userZoom, setUserZoom] = useState(1);
  const pinchState = useRef({ startDist: 0, startZoom: 1 });
  const lastTap = useRef(0);

  useEffect(() => {
    const measure = () => {
      const w = containerRef.current?.clientWidth || 540;
      setBaseScale(w / 1080);
    };
    measure();
    const ro = new ResizeObserver(measure);
    if (containerRef.current) ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  const clampZoom = (z) => Math.max(1, Math.min(3, z));

  const onTouchStart = (e) => {
    if (e.touches.length === 2) {
      const [a, b] = e.touches;
      pinchState.current.startDist = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
      pinchState.current.startZoom = userZoom;
    } else if (e.touches.length === 1) {
      const now = Date.now();
      if (now - lastTap.current < 300) {
        setUserZoom(1);
      }
      lastTap.current = now;
    }
  };
  const onTouchMove = (e) => {
    if (e.touches.length === 2) {
      e.preventDefault();
      const [a, b] = e.touches;
      const d = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
      if (pinchState.current.startDist > 0) {
        const ratio = d / pinchState.current.startDist;
        setUserZoom(clampZoom(pinchState.current.startZoom * ratio));
      }
    }
  };
  const onTouchEnd = () => {
    pinchState.current.startDist = 0;
  };
  const onWheel = (e) => {
    if (!(e.ctrlKey || e.metaKey)) return;
    e.preventDefault();
    setUserZoom((z) => clampZoom(z + (e.deltaY < 0 ? 0.1 : -0.1)));
  };

  const effectiveScale = baseScale * userZoom;

  return (
    <div
      ref={containerRef}
      className="brutal p-4 bg-white overflow-hidden select-none"
      data-testid="spotlight-preview"
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onWheel={onWheel}
      style={{ touchAction: userZoom > 1 ? "none" : "pan-y" }}
    >
      <div style={{ width: "100%", aspectRatio: "1 / 1", position: "relative", overflow: "hidden" }}>
        <div
          ref={innerRef}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: 1080,
            height: 1080,
            transform: `scale(${effectiveScale})`,
            transformOrigin: "top left",
            transition: pinchState.current.startDist > 0 ? "none" : "transform 120ms ease-out",
          }}
        >
          <InstagramCard data={data} forwardedRef={cardRef} />
        </div>
        {userZoom > 1.01 && (
          <button
            type="button"
            onClick={() => setUserZoom(1)}
            className="absolute top-2 right-2 text-[11px] uppercase tracking-widest font-black bg-black text-white px-2 py-1 rounded-md border-2 border-black"
            data-testid="spotlight-zoom-reset"
          >
            {Math.round(userZoom * 100)}% · reset
          </button>
        )}
      </div>
    </div>
  );
}

export default function SpotlightPage() {
  const [data, setData] = useState(null);
  const [history, setHistory] = useState([]);
  const [downloading, setDownloading] = useState(false);
  const cardRef = useRef(null);

  useEffect(() => {
    api.get("/spotlight").then((r) => setData(r.data));
    api.get("/spotlight/history").then((r) => setHistory(r.data)).catch(() => {});
  }, []);

  const logEvent = (action) => {
    api.post("/spotlight/events", {
      action,
      format: "instagram-square",
      spotlight_user_id: data?.user_id,
    }).catch(() => {});
  };

  const download = async () => {
    if (!cardRef.current) return;
    // Log the event FIRST so analytics isn't gated on export succeeding
    logEvent("download");
    setDownloading(true);
    try {
      const dataUrl = await toPng(cardRef.current, {
        cacheBust: true,
        pixelRatio: 1,
        width: 1080,
        height: 1080,
        skipFonts: false,
        fetchRequestInit: { mode: "cors" },
      });
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = `referpro-spotlight-${data?.name?.replace(/\s+/g, "-") || "card"}.png`;
      a.click();
      toast.success("Image downloaded — share it on Instagram!");
    } catch (e) {
      toast.error("Couldn't export image. Try again.");
    } finally {
      setDownloading(false);
    }
  };

  const share = async () => {
    if (!cardRef.current) return;
    logEvent("share");
    try {
      const dataUrl = await toPng(cardRef.current, { cacheBust: true, width: 1080, height: 1080 });
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], `referpro-spotlight.png`, { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "Top Referrer", text: "Check this out on Instagram ✨" });
      } else {
        // fallback: trigger download
        const a = document.createElement("a");
        a.href = dataUrl;
        a.download = `referpro-spotlight-${data?.name?.replace(/\s+/g, "-") || "card"}.png`;
        a.click();
        toast.success("Image downloaded — share it on Instagram!");
      }
    } catch (e) {
      toast.error("Couldn't prepare image. Try again.");
    }
  };

  if (!data) {
    return (
      <div>
        <PageHeader title="Spotlight" subtitle="Celebrate top referrers." />
        <BrutalCard className="p-10 text-center">
          <div className="text-lg font-semibold">No spotlight yet — start referring to be featured here!</div>
        </BrutalCard>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Spotlight of the Month"
        subtitle="Celebrate the team's best referrers. Download the card and share it on Instagram — earn bragging rights and bring even more great hires."
        right={
          data?.share_allowed === false ? (
            <div className="chip bg-neutral-200 text-xs">
              Sharing disabled by featured user
            </div>
          ) : (
            <div className="flex gap-2">
              <button onClick={share} className="btn-brutal btn-primary" data-testid="share-spotlight">
                <Instagram className="w-4 h-4" /> Share
              </button>
              <button onClick={download} disabled={downloading} className="btn-brutal btn-white" data-testid="download-spotlight">
                <Download className="w-4 h-4" /> {downloading ? "Preparing…" : "Download PNG"}
              </button>
            </div>
          )
        }
      />

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <PinchZoomPreview data={data} cardRef={cardRef} />
          <div className="mt-3 text-xs text-neutral-600 text-center">
            Preview auto-scaled · pinch to zoom on mobile · exported image is 1080×1080 (perfect for Instagram)
          </div>
        </div>

        <div className="space-y-4">
          <BrutalCard className="p-6 bg-brand-yellow">
            <div className="flex items-center gap-3 mb-3">
              <Sparkles className="w-6 h-6" />
              <h3 className="font-display font-black text-xl">This month's star</h3>
            </div>
            <div className="flex items-center gap-3">
              <img
                src={data.avatar_url || `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(data.name)}`}
                alt=""
                className="w-16 h-16 rounded-2xl border-2 border-black object-cover"
              />
              <div>
                <div className="font-display font-black text-xl leading-tight">{data.name}</div>
                <div className="text-xs text-neutral-700">{data.team || data.department_name || "—"}</div>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2 mt-4 text-center">
              <div className="border-2 border-black rounded-xl p-2 bg-white">
                <Coins className="w-4 h-4 mx-auto mb-1" />
                <div className="font-display font-black text-xl">{data.points}</div>
                <div className="text-[10px] uppercase tracking-widest font-bold">Points</div>
              </div>
              <div className="border-2 border-black rounded-xl p-2 bg-white">
                <Users className="w-4 h-4 mx-auto mb-1" />
                <div className="font-display font-black text-xl">{data.total_referrals}</div>
                <div className="text-[10px] uppercase tracking-widest font-bold">Refs</div>
              </div>
              <div className="border-2 border-black rounded-xl p-2 bg-white">
                <Trophy className="w-4 h-4 mx-auto mb-1" />
                <div className="font-display font-black text-xl">{data.hired_referrals}</div>
                <div className="text-[10px] uppercase tracking-widest font-bold">Hires</div>
              </div>
            </div>
            <Chip color="bg-brand-orange text-white">
              <span className="text-[10px]">{data.auto ? "Auto-selected" : "Admin pick"}</span>
            </Chip>
          </BrutalCard>

          <BrutalCard className="p-6">
            <h3 className="font-display font-black text-lg mb-2">Share etiquette</h3>
            <ul className="text-sm space-y-2 text-neutral-700">
              <li>• Tag the featured teammate (with their consent)</li>
              <li>• Use #MadeAtReferPro to amplify reach</li>
              <li>• Pair with a brief note about your culture</li>
              <li>• Link your jobs page in bio</li>
            </ul>
          </BrutalCard>
        </div>
      </div>

      {/* No duplicate; card inside preview is the source of truth */}

      {/* Hall of Fame */}
      {history.length > 0 && (
        <div className="mt-12">
          <div className="flex items-end justify-between mb-4">
            <div>
              <h2 className="font-display font-black text-3xl sm:text-4xl">Hall of Fame</h2>
              <p className="text-sm text-neutral-700 mt-1">Past monthly winners — scroll to see the legends.</p>
            </div>
            <div className="chip bg-brand-yellow" data-testid="hof-count">
              {history.length} {history.length === 1 ? "winner" : "winners"}
            </div>
          </div>
          <div className="flex gap-5 overflow-x-auto pb-3 snap-x snap-mandatory" data-testid="hof-carousel">
            {history.map((h) => {
              const [y, m] = (h.period || "").split("-");
              const monthName = new Date(parseInt(y), parseInt(m) - 1, 1).toLocaleString("en", { month: "long" });
              return (
                <div
                  key={h.period}
                  className="flex-shrink-0 w-72 snap-start brutal brutal-hover p-5 bg-white"
                  data-testid={`hof-card-${h.period}`}
                >
                  <div className="flex items-center gap-3">
                    <img
                      src={h.avatar_url || `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(h.name)}`}
                      alt=""
                      className="w-14 h-14 rounded-2xl border-2 border-black object-cover"
                    />
                    <div className="min-w-0">
                      <div className="text-[11px] uppercase tracking-widest font-bold text-neutral-600">
                        {monthName} {y}
                      </div>
                      <div className="font-display font-black text-lg leading-tight truncate">{h.name}</div>
                      <div className="text-xs text-neutral-600 truncate">
                        {h.team || h.department_name || "—"}
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2 mt-4 text-center">
                    <div className="border-2 border-black rounded-xl p-2 bg-brand-yellow">
                      <div className="font-display font-black text-lg leading-none">{h.points}</div>
                      <div className="text-[9px] uppercase tracking-widest font-bold">pts</div>
                    </div>
                    <div className="border-2 border-black rounded-xl p-2 bg-white">
                      <div className="font-display font-black text-lg leading-none">{h.total_referrals}</div>
                      <div className="text-[9px] uppercase tracking-widest font-bold">refs</div>
                    </div>
                    <div className="border-2 border-black rounded-xl p-2 bg-brand-green">
                      <div className="font-display font-black text-lg leading-none">{h.hired_referrals}</div>
                      <div className="text-[9px] uppercase tracking-widest font-bold">hires</div>
                    </div>
                  </div>
                  <Chip color={h.source === "manual" ? "bg-brand-orange text-white" : "bg-white"}>
                    <span className="text-[10px]">{h.source === "manual" ? "Admin pick" : "Auto"}</span>
                  </Chip>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
